<template>
  <div class="bg-surface-level-50 dark:bg-surface-level-900 min-h-screen">
    <!-- Search Navigation (without filters for store pages) -->
    <SearchNavigation
      ref="searchNavigationRef"
      :show-filters="false"
      :active-filters-count="0"
      :store="storeForSearchNavigation ?? {}"
      :is-store-unavailable="!canOrder"
      :store-unavailable-message="cartUnavailableMessage"
      :check-availability="checkCartAvailability"
      :menus="menus"
      @open-mobile-event-details="openMobileEventDetails"
      @availability-failed="handleCartAvailabilityFailed"
      @edit-item="openEditCartItemModal"
    />

    <!-- Mobile Event Details Section -->
    <MobileEventDetailsButton @open="openMobileEventDetails" />

    <!-- Store header: back CTA on all breakpoints; empty spacer only md+ when no back (no dead space on mobile) -->
    <header class="bg-surface-clear dark:bg-surface-clear">
      <div class="lg:px-8 max-w-7xl mx-auto px-4 sm:px-6">
        <div
          class="flex items-center"
          :class="backButtonVisible ? 'min-h-8' : 'hidden sm:flex sm:min-h-8'"
        >
          <button
            v-if="backButtonVisible"
            type="button"
            class="cursor-pointer flex hover:text-primary-dark items-center space-x-2 text-primary text-sm transition-colors"
            @click="goBack"
          >
            <svg
              class="h-3 w-3"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M15 19l-7-7 7-7"
              />
            </svg>

            <span>{{ backButtonText }}</span>
          </button>
        </div>
      </div>
    </header>

    <!-- Loading state -->
    <div
      v-if="loading || (menusLoading && !hasLoadedMenus)"
      class="flex items-center justify-center min-h-screen"
    >
      <div class="text-center">
        <div class="animate-spin border-b-2 border-primary h-12 mb-4 mx-auto rounded-full w-12" />

        <p class="dark:text-content-level-400 text-content-level-600">
          {{ $t('commerce.storeSlug.loadingStoreDetails') }}
        </p>
      </div>
    </div>

    <!-- Error state -->
    <div
      v-else-if="error"
      class="flex items-center justify-center min-h-screen"
    >
      <div class="text-center">
        <div class="mb-4 text-6xl text-danger-500">
          {{ $t('commerce.storeSlug.warningSymbol') }}
        </div>

        <h2 class="dark:text-content-inverse font-bold mb-2 text-2xl text-content-level-900">
          {{ $t('commerce.storeSlug.somethingWentWrong') }}
        </h2>

        <p class="dark:text-content-level-400 mb-4 text-content-level-600">
          {{ error }}
        </p>

        <button
          type="button"
          class="bg-primary hover:bg-primary-dark px-6 py-3 rounded-lg text-content-inverse transition-colors"
          @click="fetchStoreDetails"
        >
          {{ $t('commerce.storeSlug.tryAgain') }}
        </button>
      </div>
    </div>

    <!-- Temporarily closed state -->
    <div
      v-else-if="isFullPageBlockVisible"
      class="flex items-center justify-center min-h-screen"
    >
      <AppErrorState
        :icon="Clock"
        :title="$t('commerce.storeSlug.temporaryClosureTitle')"
        :message="temporarilyClosedStoreMessage"
        :primary-action="temporaryClosurePrimaryAction"
      />
    </div>

    <!-- Store content -->
    <div
      v-else-if="store"
      class="space-y-6"
    >
      <!-- Store banner and info -->
      <div class="lg:px-8 max-w-7xl mx-auto px-4 sm:px-6">
        <div class="relative">
          <!-- Banner image -->
          <div
            class="bg-gradient-to-r from-surface-level-400 h-64 overflow-visible relative rounded-lg to-surface-level-600"
            :class="store.logo ? 'mb-8' : ''"
          >
            <nuxt-img
              v-if="store.cover"
              :src="store.cover"
              v-bind="storeImageAttrs"
              class="h-full object-cover rounded-lg w-full"
              quality="80"
              format="webp"
              width="1920"
              height="640"
            />

            <div
              v-else
              class="bg-gradient-to-r from-surface-level-400 h-full to-surface-level-600 w-full"
            />

            <!-- Save (favorite) overlay -->
            <button
              type="button"
              class="absolute backdrop-blur bg-surface-canvas/95 cursor-pointer dark:bg-surface-level-900/80 dark:hover:bg-surface-level-900 dark:ring-focus-inverse/10 h-10 hover:bg-surface-canvas inline-flex items-center justify-center right-3 ring-1 ring-focus-ink/5 rounded-full shadow-md top-3 transition-colors w-10 z-(--z-index-ds-lifted)"
              :aria-pressed="isSaved"
              :aria-label="
                isSaved
                  ? $t('commerce.storeSlug.removeFavoriteLabel')
                  : $t('commerce.storeSlug.saveFavoriteLabel')
              "
              @click="toggleSave"
            >
              <Heart
                class="h-5 transition-colors w-5"
                :class="
                  isSaved
                    ? 'fill-icon-current text-danger-500'
                    : 'text-content-level-700 dark:text-content-level-300'
                "
              />
            </button>

            <!-- Logo overlay - bleeds out at bottom -->
            <div
              v-if="store.logo"
              class="-bottom-10 -translate-x-1/2 absolute bg-warning-50 dark:bg-surface-level-900 left-1/2 p-1 rounded-full sm:left-6 sm:translate-x-0 z-(--z-index-ds-raised)"
            >
              <nuxt-img
                v-if="store.logo"
                :src="store.logo"
                v-bind="storeImageAttrs"
                class="h-20 object-cover rounded-full w-20"
                quality="80"
                format="webp"
                width="80"
                height="80"
              />
            </div>
          </div>
        </div>

        <!-- Store info below banner -->
        <div
          class="bg-surface-clear border-b border-border-level-200 dark:bg-surface-clear dark:border-border-level-700"
        >
          <div class="lg:px-0 max-w-7xl mx-auto px-0 py-6 sm:px-0">
            <div class="flex flex-col lg:flex-row lg:items-start lg:justify-between">
              <!-- Left side: Store name, rating, address -->
              <div class="flex-1 lg:text-left text-center">
                <h1
                  class="dark:text-content-inverse font-bold lg:text-3xl mb-2 text-content-level-900 text-xl"
                >
                  {{ store.name }}
                </h1>

                <div class="flex flex-col gap-2 items-center lg:items-start mb-2">
                  <!-- Row 1: badge, reviews, Info -->
                  <div class="flex flex-wrap gap-4 items-center justify-center lg:justify-start">
                    <div
                      v-if="store.is_reliability_superstar"
                      class="bg-secondary flex font-medium items-center px-3 py-1 rounded-full space-x-1 text-content-inverse text-sm"
                    >
                      <SparklesIcon class="h-4 w-4" />

                      <span>
                        {{ $t('commerce.storeSlug.reliabilitySuperstar') }}
                      </span>
                    </div>

                    <div class="flex items-center">
                      <svg
                        v-if="hasReviews"
                        class="h-5 text-warning-400 w-5"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"
                        />
                      </svg>

                      <span
                        v-if="hasReviews"
                        class="dark:text-content-inverse font-semibold leading-5 lg:leading-6 lg:text-[15px] ml-1 text-[13px] text-content-level-900"
                        >{{ store.average_rating }}</span
                      >

                      <div
                        class="dark:text-content-level-400 flex flex-wrap gap-x-2 items-center ml-1 text-content-level-600 text-xs"
                      >
                        <template v-if="hasReviews">
                          <span
                            class="dark:text-content-level-400 leading-5 lg:leading-6 lg:text-[15px] text-[13px] text-content-level-600"
                            >({{ store.review_count }})</span
                          >

                          <span class="mx-1">·</span>
                        </template>

                        <button
                          type="button"
                          class="cursor-pointer dark:hover:text-content-level-200 dark:text-content-level-400 focus:outline-none font-normal gap-1 hover:text-content-level-900 inline-flex items-center leading-5 lg:leading-6 lg:text-[15px] text-[13px] text-content-level-600 transition-colors underline"
                          @click="openStoreInfoModal"
                        >
                          <span>
                            {{ $t('commerce.storeSlug.info') }}
                          </span>

                          <InformationCircleIcon
                            class="h-4 shrink-0 w-4"
                            aria-hidden="true"
                          />
                        </button>
                      </div>
                    </div>
                  </div>

                  <!-- Row 2: address, locations -->
                  <div
                    class="flex flex-col items-center justify-center lg:justify-start sm:flex-row sm:items-center sm:space-x-2 sm:space-y-0 space-y-1"
                  >
                    <div
                      class="dark:text-content-level-400 leading-5 lg:leading-6 lg:text-[15px] sm:text-left text-[13px] text-center text-content-level-600"
                    >
                      {{ storeAddress }}
                    </div>

                    <span
                      v-if="hasOtherLocations"
                      class="dark:text-content-level-500 hidden sm:inline text-content-level-400"
                      >·</span
                    >

                    <!-- Desktop: location dropdown -->
                    <UDropdownMenu
                      v-if="hasOtherLocations"
                      :items="otherLocationMenuItems"
                      :content="locationMenuContent"
                      :ui="locationMenuUi"
                    >
                      <button
                        type="button"
                        class="cursor-pointer focus:outline-none hidden hover:text-primary-dark items-center leading-5 lg:flex lg:leading-6 lg:text-[15px] space-x-2 text-[13px] text-primary transition-colors"
                      >
                        <span>
                          {{ $t('commerce.storeSlug.lookingForAnotherLocation') }}
                        </span>

                        <ChevronDownIcon class="h-4 w-4" />
                      </button>

                      <template #view-all>
                        <span
                          class="flex-1 font-medium text-left text-sm"
                          aria-hidden="true"
                        >
                          {{ $t('commerce.storeSlug.viewAll') }}
                        </span>

                        <span class="sr-only">
                          {{
                            $t('commerce.storeSlug.viewAllLocationsLabel', {
                              brandName: locationsData?.brand.name ?? ''
                            })
                          }}
                        </span>

                        <ChevronRightIcon class="h-4 w-4" />
                      </template>
                    </UDropdownMenu>

                    <!-- Mobile: button opens bottom sheet -->
                    <button
                      v-if="hasOtherLocations"
                      type="button"
                      class="cursor-pointer flex focus:outline-none hover:text-primary-dark items-center leading-5 lg:hidden lg:leading-6 lg:text-[15px] space-x-2 text-[13px] text-primary transition-colors"
                      @click="openLocationsModal"
                    >
                      <span>
                        {{ $t('commerce.storeSlug.lookingForAnotherLocation') }}
                      </span>

                      <ChevronDownIcon class="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>

              <!-- Right side: store categories -->
              <div class="flex flex-col items-center lg:items-end mt-0 space-y-3">
                <!-- Store Categories -->
                <div
                  v-if="storeCategories.length > 0"
                  class="dark:text-content-level-400 flex flex-wrap gap-x-2 gap-y-2 items-center justify-center lg:justify-end lg:text-sm text-content-level-600 text-xs"
                >
                  <div
                    v-for="(category, index) in storeCategories"
                    :key="category.id || category.name"
                    class="contents"
                  >
                    <span class="flex items-center">
                      <!-- Category icon beside the category label. -->
                      <span>{{ category.name }}</span>
                    </span>

                    <span
                      v-if="index < storeCategories.length - 1"
                      class="mx-1"
                      >·</span
                    >
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Menu type toggle + search -->
      <div
        v-if="hasLoadedMenus && !menusError"
        class="lg:px-8 max-w-7xl mb-0 mx-auto px-4 sm:px-6"
      >
        <div class="flex flex-col gap-4 py-3 sm:flex-row sm:items-end sm:justify-between">
          <div class="min-w-0">
            <div
              v-if="menuTypeOptions.length > 0"
              class="bg-surface-level-200 dark:bg-surface-level-800 grid rounded-full sm:inline-grid sm:w-auto w-full"
              :class="menuTypeOptions.length > 1 ? 'grid-cols-2' : 'grid-cols-1'"
              role="group"
              :aria-label="$t('commerce.storeSlug.menuTypeLabel')"
            >
              <button
                v-for="option in menuTypeOptions"
                :key="option.value"
                type="button"
                :disabled="menusLoading"
                class="cursor-pointer disabled:cursor-not-allowed disabled:opacity-60 font-semibold px-4 py-2 rounded-full sm:min-w-28 text-center text-sm transition-colors"
                :class="[
                  selectedMenuType === option.value
                    ? 'bg-primary text-content-inverse shadow-sm hover:bg-primary-dark'
                    : 'text-content-level-600 hover:text-content-level-900 dark:text-content-level-300 dark:hover:text-content-inverse'
                ]"
                @click="() => onMenuTypeChange(option.value)"
              >
                {{ option.label }}
              </button>
            </div>

            <template v-if="menusLoading || hasMenus">
              <p
                class="dark:text-content-inverse font-semibold mt-4 sm:text-xl text-content-level-900 text-lg tracking-tight"
              >
                {{ $t('commerce.storeSlug.labelMenus', { label: selectedMenuTypeLabel }) }}
              </p>

              <p class="dark:text-content-level-400 mt-1 text-content-level-500 text-sm">
                {{ $t('commerce.storeSlug.selectAMenuFromTheSidebarTo') }}
              </p>
            </template>

            <div
              v-else
              class="dark:text-content-level-400 mt-4 text-content-level-600 text-sm"
            >
              {{ emptyMenusTitle }}
            </div>
          </div>

          <div
            v-if="hasMenus"
            class="hidden sm:block sm:max-w-xs sm:w-full"
          >
            <div
              class="-outline-offset-1 bg-surface-canvas dark:bg-surface-canvas/5 dark:outline-focus-inverse/10 dark:text-content-inverse flex focus-within:-outline-offset-2 focus-within:outline-2 focus-within:outline-primary gap-2 h-10 items-center outline outline-1 outline-focus-level-300 px-3 rounded-md text-content-level-900"
            >
              <Search class="dark:text-content-level-500 h-4 text-content-level-400 w-4" />

              <input
                v-model="menuSearchQuery"
                type="text"
                :placeholder="$t('commerce.storeSlug.menuSearchPlaceholder')"
                class="bg-surface-clear dark:text-content-inverse font-medium outline-none placeholder:text-content-level-400 text-sm w-full"
              />

              <button
                v-if="menuSearchQuery"
                type="button"
                class="cursor-pointer dark:hover:text-content-inverse hover:text-content-level-700 text-content-level-400"
                :aria-label="$t('commerce.storeSlug.clearSearchLabel')"
                @click="clearMenuSearch"
              >
                <X class="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Menu loading has its own retry without refetching store details. -->
      <div
        v-if="menusError"
        class="lg:px-8 max-w-7xl mx-auto px-4 py-16 sm:px-6"
      >
        <EmptyState
          variant="error"
          :title="$t('commerce.storeSlug.somethingWentWrong')"
          :description="menusError"
          :full-height="false"
          show-action
          :action-label="$t('commerce.storeSlug.tryAgain')"
          container-class="!rounded-3xl"
          @action="fetchStoreMenus"
        />
      </div>

      <div
        v-if="hasLoadedMenus && !hasMenus && !menusLoading && !menusError"
        class="lg:px-8 max-w-7xl mx-auto px-4 py-16 sm:px-6"
      >
        <EmptyState
          variant="empty"
          :title="emptyMenusTitle"
          :description="$t('commerce.storeSlug.emptyMenusDescription')"
          :resource-label="$t('commerce.storeSlug.menusResourceLabel')"
          :full-height="false"
          container-class="!rounded-3xl"
        />
      </div>

      <!-- Skeleton: shown while switching menu type after initial load -->
      <div
        v-if="menusLoading && hasLoadedMenus"
        class="lg:px-8 max-w-7xl mx-auto pb-8 px-4 sm:px-6"
      >
        <!-- Mobile pill skeletons -->
        <div class="flex gap-2 lg:hidden overflow-hidden pb-2 pt-2">
          <div
            v-for="n in 3"
            :key="`sk-mobile-pill-${n}`"
            class="animate-pulse bg-surface-level-200 dark:bg-surface-level-700 h-9 rounded-full shrink-0 w-24"
          />
        </div>

        <div class="flex gap-8 items-start mt-5 store-content-height">
          <!-- Sidebar skeleton -->
          <aside
            class="[scrollbar-width:thin] hidden lg:block overflow-y-auto overscroll-contain pr-1 self-start shrink-0 sticky store-sidebar-height top-[80px] w-60"
          >
            <div class="pb-2 px-3">
              <div
                class="animate-pulse bg-surface-level-200 dark:bg-surface-level-700 h-4 rounded w-24"
              />
            </div>

            <div class="space-y-0.5">
              <div
                v-for="n in 6"
                :key="`sk-menu-${n}`"
                class="flex items-center px-3 py-2"
              >
                <div
                  class="animate-pulse bg-surface-level-200 dark:bg-surface-level-700 h-4 rounded"
                  :class="n % 3 === 0 ? 'w-20' : n % 2 === 0 ? 'w-32' : 'w-28'"
                />
              </div>
            </div>
          </aside>

          <!-- Content area skeleton -->
          <div class="flex-1 min-w-0 space-y-10">
            <div>
              <div
                class="animate-pulse bg-surface-level-200 dark:bg-surface-level-700 h-7 rounded w-48"
              />

              <div
                class="animate-pulse bg-surface-level-200 dark:bg-surface-level-700 h-4 mt-2 rounded w-72"
              />
            </div>

            <section
              v-for="n in 2"
              :key="`sk-section-${n}`"
              class="space-y-4"
            >
              <div
                class="animate-pulse bg-surface-level-200 dark:bg-surface-level-700 h-6 rounded w-32"
              />

              <div class="gap-3 grid grid-cols-1 lg:grid-cols-2">
                <div
                  v-for="m in 4"
                  :key="`sk-card-${n}-${m}`"
                  class="bg-surface-canvas border border-border-level-200 dark:bg-surface-level-800 dark:border-border-level-700 overflow-hidden rounded-xl"
                >
                  <div class="flex h-[145px]">
                    <div class="flex-1 p-3 space-y-2">
                      <div
                        class="animate-pulse bg-surface-level-200 dark:bg-surface-level-700 h-5 rounded w-32"
                      />

                      <div
                        class="animate-pulse bg-surface-level-200 dark:bg-surface-level-700 h-4 rounded w-20"
                      />

                      <div
                        class="animate-pulse bg-surface-level-200 dark:bg-surface-level-700 h-3 rounded w-full"
                      />

                      <div
                        class="animate-pulse bg-surface-level-200 dark:bg-surface-level-700 h-3 rounded w-3/4"
                      />
                    </div>

                    <div
                      class="animate-pulse bg-surface-level-200 dark:bg-surface-level-700 h-full shrink-0 w-[145px]"
                    />
                  </div>
                </div>
              </div>
            </section>
          </div>
        </div>
      </div>

      <!-- Mobile sticky menu + categories (sections of current menu) -->
      <div
        v-if="hasMenus && !menusLoading"
        class="backdrop-blur-xl bg-surface-canvas/95 dark:bg-surface-level-900/95 lg:hidden sticky top-[61px] z-(--z-index-ds-bar)"
      >
        <div class="lg:px-8 max-w-7xl mx-auto px-4 sm:px-6">
          <div
            v-if="hasMultipleMenus"
            class="pb-2 pt-2"
          >
            <InputSelect
              :model-value="selectedMenuId ?? ''"
              :options="mobileMenuOptions"
              :ariaLabel="$t('commerce.storeSlug.menuSelectPlaceholder')"
              remove-top-spacing
              :placeholder="$t('commerce.storeSlug.menuSelectPlaceholder')"
              @update:model-value="onMobileMenuSelect"
            />
          </div>

          <div
            v-else-if="selectedMenu"
            class="pb-2 pt-3"
          >
            <h2 class="dark:text-content-inverse font-semibold text-content-level-900 text-lg">
              {{ selectedMenu.name }}
            </h2>
          </div>

          <div
            v-if="visibleMenuCategories.length > 0"
            class="-mx-4 [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden [scrollbar-width:none] border-b border-border-level-200 dark:border-border-level-700 overflow-x-auto pt-2 px-4"
          >
            <div class="flex gap-5">
              <button
                v-for="category in visibleMenuCategories"
                :key="`cat-pill-${category.id}`"
                type="button"
                class="border-b-2 pb-2 shrink-0 text-sm transition-colors whitespace-nowrap"
                :class="[
                  activeCategory === category.id
                    ? 'border-primary font-semibold text-primary'
                    : 'border-border-clear font-medium text-content-level-600 hover:text-content-level-900 dark:text-content-level-400 dark:hover:text-content-inverse'
                ]"
                @click="() => scrollToCategory(category.id)"
              >
                {{ category.name }}
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Sidebar + content -->
      <div
        v-if="hasMenus && !menusLoading"
        class="lg:px-8 max-w-7xl mx-auto pb-8 px-4 sm:px-6"
      >
        <div class="flex gap-8 items-start mt-5 store-content-height">
          <!-- Sidebar -->
          <aside
            class="[scrollbar-width:thin] hidden lg:block overflow-y-auto overscroll-contain pr-1 self-start shrink-0 sticky store-sidebar-height top-[80px] w-60 z-(--z-index-ds-lifted)"
            @wheel.stop
            @touchmove.stop
          >
            <nav>
              <div
                v-for="(menu, menuIndex) in menus"
                :key="`sb-menu-${menu.id}`"
                class="border overflow-hidden rounded-xl"
                :class="[
                  menuIndex > 0 && 'mt-3',
                  String(selectedMenuId) === String(menu.id)
                    ? 'border-primary/30 dark:border-primary/40'
                    : 'border-border-level-200 dark:border-border-level-700'
                ]"
              >
                <!-- Menu name header -->
                <div
                  class="flex items-stretch transition-colors w-full"
                  :class="[
                    String(selectedMenuId) === String(menu.id)
                      ? 'bg-primary/10 dark:bg-primary/20'
                      : 'hover:bg-surface-level-50 dark:hover:bg-surface-level-700/30'
                  ]"
                >
                  <button
                    type="button"
                    class="cursor-pointer flex flex-1 items-center min-w-0 px-3 py-3 text-left"
                    @click="() => changeMenu(menu.id)"
                  >
                    <span class="min-w-0">
                      <span
                        class="block font-semibold text-sm truncate"
                        :class="[
                          getMenuUnavailableReason(menu)
                            ? 'text-content-level-400 dark:text-content-level-500'
                            : String(selectedMenuId) === String(menu.id)
                              ? 'text-primary'
                              : 'text-content-level-500 dark:text-content-level-400'
                        ]"
                      >
                        {{ menu.name }}
                      </span>

                      <span
                        v-if="getMenuAvailabilityStatusLine(menu)"
                        class="block dark:text-content-level-400 font-medium mt-0.5 text-content-level-500 text-xs truncate"
                      >
                        {{ getMenuAvailabilityStatusLine(menu) }}
                      </span>
                    </span>
                  </button>

                  <button
                    type="button"
                    class="cursor-pointer flex items-center justify-center px-3 shrink-0"
                    :aria-label="
                      isMenuExpanded(menu.id)
                        ? $t('commerce.storeSlug.collapseSectionsLabel')
                        : $t('commerce.storeSlug.expandSectionsLabel')
                    "
                    :aria-expanded="isMenuExpanded(menu.id)"
                    @click="() => toggleMenuExpansion(menu.id)"
                  >
                    <ChevronDownIcon
                      class="h-4 shrink-0 transition-transform w-4"
                      :class="[
                        isMenuExpanded(menu.id) && 'rotate-180',
                        String(selectedMenuId) === String(menu.id)
                          ? 'text-primary/70'
                          : 'text-content-level-400 dark:text-content-level-500'
                      ]"
                    />
                  </button>
                </div>

                <!-- Category list -->
                <div v-if="isMenuExpanded(menu.id) && (menu.categories?.length ?? 0) > 0">
                  <button
                    v-for="category in menu.categories"
                    :key="`sb-cat-${menu.id}-${category.id}`"
                    type="button"
                    class="border-l-2 cursor-pointer flex items-center pl-3 pr-3 py-2 text-left text-sm transition-colors w-full"
                    :class="[
                      String(selectedMenuId) === String(menu.id) && activeCategory === category.id
                        ? 'border-l-primary font-medium text-primary'
                        : 'border-l-border-clear font-normal text-content-level-600 dark:text-content-level-400 hover:bg-surface-level-50 dark:hover:bg-surface-level-700/30 hover:text-content-level-900 dark:hover:text-content-inverse'
                    ]"
                    @click="() => changeMenuAndScrollToCategory(menu.id, category.id)"
                  >
                    <span class="truncate">{{ category.name }}</span>
                  </button>
                </div>
              </div>
            </nav>
          </aside>

          <!-- Content area -->
          <div class="flex-1 min-w-0 space-y-10">
            <!-- Search mode header -->
            <template v-if="menuSearchQuery.trim()">
              <div>
                <p class="font-semibold text-primary text-sm tracking-wider uppercase">
                  {{ selectedMenu?.name }}
                </p>

                <h2
                  class="dark:text-content-inverse font-bold text-2xl text-content-level-900 tracking-tight"
                >
                  {{ $t('commerce.storeSlug.resultsForQuery', { query: menuSearchQuery }) }}
                </h2>

                <p class="dark:text-content-level-400 mt-1 text-content-level-500 text-sm">
                  {{ $t('commerce.storeSlug.resultsStayWithinTheSelectedMenuSwitch') }}
                </p>
              </div>

              <div
                v-if="!hasSearchResults"
                class="border border-border-level-300 border-dashed dark:border-border-level-700 p-8 rounded-xl text-center"
              >
                <p class="dark:text-content-inverse font-bold text-content-level-900">
                  {{ $t('commerce.storeSlug.noResultsInThisMenu') }}
                </p>

                <p class="dark:text-content-level-400 mt-1 text-content-level-500 text-sm">
                  {{ $t('commerce.storeSlug.tryAnotherMenuOrAdjustYourSearch') }}
                </p>
              </div>
            </template>

            <!-- Default menu header -->
            <div
              v-else
              class="hidden lg:block"
            >
              <h2
                class="dark:text-content-inverse font-bold text-2xl text-content-level-900 tracking-tight"
              >
                {{ selectedMenu?.name }}
              </h2>

              <p
                v-if="selectedMenuAvailabilityWindowLabel"
                class="dark:text-content-level-400 font-medium mt-2 text-content-level-500 text-sm"
              >
                {{ selectedMenuAvailabilityWindowLabel }}
              </p>

              <p
                v-if="formatMenuDescriptionText(selectedMenu?.description)"
                class="dark:text-content-level-400 mt-2 text-content-level-500 text-sm"
              >
                {{ formatMenuDescriptionText(selectedMenu?.description) }}
              </p>
            </div>

            <!-- Filter empty state (no search, but no items matching filters) -->
            <EmptyState
              v-if="!menuSearchQuery.trim() && visibleMenuCategories.length === 0 && isFilterActive"
              key="empty-state-1"
              variant="filter"
              :title="$t('commerce.storeSlug.noFilteredItemsTitle')"
              :description="$t('commerce.storeSlug.filterAdjustmentHelp')"
              :resource-label="$t('commerce.storeSlug.menuItemsResourceLabel')"
              show-action
              :action-label="$t('commerce.storeSlug.clearFiltersLabel')"
              :full-height="false"
              container-class="!rounded-3xl"
              @action="clearFilters"
            />

            <!-- Empty menu (no categories at all) -->
            <EmptyState
              v-else-if="!menuSearchQuery.trim() && visibleMenuCategories.length === 0"
              key="empty-state-2"
              variant="empty"
              :title="$t('commerce.storeSlug.emptyMenuTitle')"
              :description="$t('commerce.storeSlug.emptyMenuDescription')"
              :resource-label="$t('commerce.storeSlug.menuItemsResourceLabel')"
              :full-height="false"
              container-class="!rounded-3xl"
            />

            <!-- Featured category sections (horizontal slider) -->
            <section
              v-for="category in displayedFeaturedCategories"
              :id="`category-${category.id}`"
              :key="`featured-section-${category.id}`"
              class="lg:scroll-mt-32 scroll-mt-44 space-y-4"
            >
              <div class="flex gap-4 items-start justify-between">
                <div class="min-w-0">
                  <h3 class="dark:text-content-inverse font-bold text-content-level-900 text-xl">
                    {{ category.name }}
                  </h3>

                  <p
                    v-if="formatMenuDescriptionText(category.description)"
                    class="dark:text-content-level-400 leading-relaxed max-w-4xl mt-1.5 text-content-level-600 text-sm"
                  >
                    {{ formatMenuDescriptionText(category.description) }}
                  </p>
                </div>

                <div
                  v-if="featuredCarouselHasPagination(category.id)"
                  class="flex gap-2 shrink-0"
                >
                  <button
                    type="button"
                    class="bg-surface-canvas border border-border-level-200 dark:bg-surface-level-800 dark:border-border-level-600 dark:hover:bg-surface-level-700 dark:text-content-inverse h-9 hover:bg-surface-level-50 inline-flex items-center justify-center rounded-full shadow-sm text-content-level-900 transition-colors w-9"
                    :aria-label="$t('commerce.storeSlug.scrollFeaturedLeftLabel')"
                    @click="() => scrollFeaturedPrev(category.id)"
                  >
                    <ChevronLeftIcon class="h-5 w-5" />
                  </button>

                  <button
                    type="button"
                    class="bg-surface-canvas border border-border-level-200 dark:bg-surface-level-800 dark:border-border-level-600 dark:hover:bg-surface-level-700 dark:text-content-inverse h-9 hover:bg-surface-level-50 inline-flex items-center justify-center rounded-full shadow-sm text-content-level-900 transition-colors w-9"
                    :aria-label="$t('commerce.storeSlug.scrollFeaturedRightLabel')"
                    @click="() => scrollFeaturedNext(category.id)"
                  >
                    <ChevronRightIcon class="h-5 w-5" />
                  </button>
                </div>
              </div>

              <div
                :ref="(el) => setFeaturedCarouselRef(category.id, el)"
                class="[-ms-overflow-style:none] [scrollbar-width:none] featured-carousel flex gap-5 overflow-x-auto pb-1 scroll-smooth scrollbar-hide w-full"
              >
                <article
                  v-for="item in getCategoryItems(category.id)"
                  :key="`featured-item-${item.id}`"
                  class="featured-carousel-item group lg:w-[calc((100%-3.75rem)/4)] shrink-0 sm:w-[200px] w-[calc((100%-1.25rem)/2)]"
                  :class="[
                    canInteractWithMenuItems &&
                      (selectedMenuUnavailableReason ? 'cursor-not-allowed' : 'cursor-pointer')
                  ]"
                  @click="
                    () =>
                      canInteractWithMenuItems && !selectedMenuUnavailableReason
                        ? openMenuItemModal(item)
                        : undefined
                  "
                >
                  <div
                    class="aspect-square bg-surface-level-100 dark:bg-surface-level-700 overflow-hidden relative rounded-xl"
                  >
                    <nuxt-img
                      v-if="shouldShowMenuItemImage(item)"
                      :src="getMenuItemImage(item)!"
                      v-bind="menuItemImageAttrs(item.name)"
                      class="absolute h-full inset-0 object-center object-cover w-full"
                      quality="80"
                      format="webp"
                      width="400"
                      height="400"
                      @error="() => markMenuItemImageAsFailed(item.id)"
                    />

                    <div
                      v-if="!shouldShowMenuItemImage(item)"
                      class="absolute flex inset-0 items-center justify-center pointer-events-none"
                    >
                      <Utensils class="h-10 text-content-level-400 w-10" />
                    </div>

                    <span
                      v-if="item.is_popular"
                      class="absolute bg-accent font-semibold inline-flex items-center leading-tight left-2 pointer-events-none px-2 py-[2px] rounded-md shadow-sm text-content-inverse text-xs top-2 z-(--z-index-ds-raised)"
                    >
                      {{ $t('commerce.storeSlug.popular') }}
                    </span>

                    <button
                      v-if="canInteractWithMenuItems && !selectedMenuUnavailableReason"
                      type="button"
                      :aria-label="$t('commerce.storeSlug.addItemLabel', { name: item.name })"
                      class="absolute bg-surface-canvas bottom-2.5 focus-visible:outline-2 focus-visible:outline-focus-ink focus-visible:outline-offset-2 group-hover:bg-surface-level-100 h-8 inline-flex items-center justify-center right-2.5 ring-1 ring-focus-ink/5 rounded-full shadow-md text-content-level-900 transition-colors w-8 z-(--z-index-ds-raised)"
                      @click.stop="() => openMenuItemModal(item)"
                    >
                      <svg
                        aria-hidden="true"
                        class="h-4 w-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2.25"
                          d="M12 4v16m8-8H4"
                        />
                      </svg>
                    </button>

                    <span
                      v-else-if="selectedMenuUnavailableReason"
                      class="absolute backdrop-blur-[1px] bg-surface-scrim/65 bottom-2.5 dark:bg-surface-scrim/70 dark:ring-focus-inverse/10 dark:text-content-inverse font-semibold inset-x-2 px-2 py-1 ring-1 ring-focus-ink/10 rounded-md shadow-sm text-center text-content-inverse text-xs z-(--z-index-ds-raised)"
                    >
                      {{ selectedMenuUnavailableReason }}
                    </span>
                  </div>

                  <p
                    class="dark:text-content-inverse font-semibold leading-5 line-clamp-2 mt-1 text-base text-content-level-900"
                  >
                    {{ item.name }}
                  </p>

                  <p class="mt-1 text-sm">
                    <span class="font-semibold text-primary">{{ formatCurrency(item.price) }}</span>

                    <span
                      v-if="getMenuItemPriceTypeLabel(item)"
                      class="dark:text-content-level-500 font-normal text-content-level-400 text-xs"
                    >
                      {{ ` ${getMenuItemPriceTypeLabel(item)}` }}
                    </span>

                    <span
                      v-if="item.serves_count"
                      class="dark:text-content-level-500 font-normal text-content-level-400 text-xs"
                      >{{ $t('commerce.storeSlug.servesCount', { count: item.serves_count }) }}
                    </span>
                  </p>
                </article>
              </div>
            </section>

            <!-- Reviews Slider -->
            <section class="lg:scroll-mt-32 scroll-mt-44 space-y-4">
              <StoreReviewsSlider
                :reviews="(storeReviewsRef?.reviews ?? []).slice(0, 4)"
                :google-reviews="(storeReviewsRef?.googleReviews ?? []).slice(0, 4)"
                see-more-link="#store-reviews"
                v-bind="storeReviewsSliderProps"
              />
            </section>

            <!-- Regular category sections -->
            <section
              v-for="category in displayedRegularCategories"
              :id="`category-${category.id}`"
              :key="`section-${category.id}`"
              class="lg:scroll-mt-32 scroll-mt-44 space-y-4"
            >
              <div>
                <h3 class="dark:text-content-inverse font-bold text-content-level-900 text-xl">
                  {{ category.name }}
                </h3>

                <p
                  v-if="formatMenuDescriptionText(category.description)"
                  class="dark:text-content-level-400 leading-relaxed max-w-4xl mt-1.5 text-content-level-600 text-sm"
                >
                  {{ formatMenuDescriptionText(category.description) }}
                </p>
              </div>

              <div class="gap-5 grid grid-cols-1 lg:gap-x-5 lg:grid-cols-2">
                <div
                  v-for="item in getCategoryItems(category.id)"
                  :key="item.id"
                  class="bg-surface-canvas border border-border-level-200 dark:bg-surface-level-800 dark:border-border-level-700 group overflow-hidden relative rounded-xl transition-all"
                  :class="[
                    canInteractWithMenuItems &&
                      (selectedMenuUnavailableReason
                        ? 'cursor-not-allowed'
                        : 'cursor-pointer hover:shadow-sm')
                  ]"
                  @click="
                    () =>
                      canInteractWithMenuItems && !selectedMenuUnavailableReason
                        ? openMenuItemModal(item)
                        : undefined
                  "
                >
                  <div class="flex h-[145px] overflow-hidden">
                    <div class="flex-1 min-h-0 min-w-0 overflow-hidden p-3 pt-4">
                      <p
                        class="dark:text-content-inverse font-semibold leading-5 line-clamp-2 text-base text-content-level-900"
                      >
                        {{ item.name }}
                      </p>

                      <p class="mt-1 text-sm">
                        <span class="font-semibold text-primary">{{
                          formatCurrency(item.price)
                        }}</span>

                        <span
                          v-if="getMenuItemPriceTypeLabel(item)"
                          class="dark:text-content-level-500 font-normal text-content-level-400 text-xs"
                        >
                          {{ ` ${getMenuItemPriceTypeLabel(item)}` }}
                        </span>

                        <span
                          v-if="item.serves_count"
                          class="dark:text-content-level-500 font-normal text-content-level-400 text-xs"
                          >{{ $t('commerce.storeSlug.servesCount', { count: item.serves_count }) }}
                        </span>
                      </p>

                      <p
                        v-if="item.description"
                        class="dark:text-content-level-300 leading-5 line-clamp-2 mt-1.5 text-content-level-600 text-sm"
                      >
                        {{ formatMenuDescriptionText(item.description) }}
                      </p>
                    </div>

                    <div
                      class="bg-surface-level-100 dark:bg-surface-level-700 flex-shrink-0 h-full min-h-0 overflow-hidden relative self-stretch w-[145px]"
                    >
                      <nuxt-img
                        v-if="shouldShowMenuItemImage(item)"
                        :src="getMenuItemImage(item)!"
                        v-bind="menuItemImageAttrs(item.name)"
                        class="absolute h-full inset-0 object-center object-cover w-full"
                        quality="80"
                        format="webp"
                        width="290"
                        height="290"
                        @error="() => markMenuItemImageAsFailed(item.id)"
                      />

                      <div
                        v-if="!shouldShowMenuItemImage(item)"
                        class="absolute flex inset-0 items-center justify-center pointer-events-none"
                      >
                        <Utensils class="h-8 text-content-level-400 w-8" />
                      </div>

                      <span
                        v-if="item.is_popular"
                        class="absolute bg-accent font-semibold inline-flex items-center leading-tight left-1.5 pointer-events-none px-2 py-[2px] rounded-md shadow-sm text-content-inverse text-xs top-1.5 z-(--z-index-ds-raised)"
                        >{{ $t('commerce.storeSlug.popular') }}</span
                      >

                      <button
                        v-if="canInteractWithMenuItems && !selectedMenuUnavailableReason"
                        type="button"
                        :aria-label="$t('commerce.storeSlug.addItemLabel', { name: item.name })"
                        class="absolute bg-surface-canvas bottom-2.5 focus-visible:outline-2 focus-visible:outline-focus-ink focus-visible:outline-offset-2 group-hover:bg-surface-level-100 h-8 inline-flex items-center justify-center right-2.5 ring-1 ring-focus-ink/5 rounded-full shadow-md text-content-level-900 transition-colors w-8 z-(--z-index-ds-raised)"
                        @click.stop="() => openMenuItemModal(item)"
                      >
                        <svg
                          aria-hidden="true"
                          class="h-4 w-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2.25"
                            d="M12 4v16m8-8H4"
                          />
                        </svg>
                      </button>

                      <span
                        v-else-if="selectedMenuUnavailableReason"
                        class="absolute backdrop-blur-[1px] bg-surface-scrim/65 bottom-2.5 dark:bg-surface-scrim/70 dark:ring-focus-inverse/10 dark:text-content-inverse font-semibold inset-x-2 px-2 py-1 ring-1 ring-focus-ink/10 rounded-md shadow-sm text-center text-content-inverse text-xs z-(--z-index-ds-raised)"
                      >
                        {{ selectedMenuUnavailableReason }}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            <!-- Reviews Section -->
            <section
              id="store-reviews"
              class="lg:scroll-mt-32 scroll-mt-44 space-y-4"
            >
              <StoreReviews
                ref="storeReviewsRef"
                :slug="slug ?? ''"
              />
            </section>
          </div>
        </div>
      </div>
    </div>

    <!-- Menu Item Modal -->
    <MenuItemModal
      :is-open="showMenuItemModal"
      :is-adding-to-cart="isAddingToCart"
      :menu-item="selectedMenuItem"
      :requires-order-details="Boolean(orderDetailsMissingReason)"
      :edit-mode="menuItemEditMode"
      v-bind="menuItemModalProps"
      @close="closeMenuItemModal"
      @add-to-cart="handleMenuItemModalResult"
      @select-date-time="openOrderDetailsModal"
    />

    <!-- Mobile Event Details Modal -->
    <MobileEventDetailsModal
      :is-open="showMobileEventDetailsModal"
      is-store-page
      :timezone="store?.timezone ?? null"
      :store="storeForSearchNavigation ?? null"
      :menus="menus"
      @close="closeMobileEventDetails"
      @update="handleMobileEventDetailsUpdate"
    />

    <!-- Availability Error Modal -->
    <AvailabilityErrorModal
      :is-open="showAvailabilityErrorModal"
      :error-data="availabilityErrorData"
      v-bind="availabilityErrorProps"
      @close="closeAvailabilityError"
    />

    <StoreInfoModal
      :is-open="showStoreInfoModal"
      :store="store"
      :categories="storeCategories"
      @close="closeStoreInfoModal"
    />

    <!-- Mobile: Other locations modal -->
    <BaseModal
      :model-value="showLocationsModal"
      :title="$t('commerce.storeSlug.countLocations', { count: otherLocationsCount })"
      max-width-class="max-w-none"
      panel-class="overflow-hidden rounded-t-2xl"
      :show-close-button="false"
      @close="closeLocationsModal"
    >
      <template #content>
        <div
          class="border-b border-border-level-200 dark:border-border-level-700 flex items-center justify-between px-6 py-4"
        >
          <h3 class="dark:text-content-inverse font-semibold text-content-level-900 text-lg">
            {{ $t('commerce.storeSlug.countLocations', { count: otherLocationsCount }) }}
          </h3>

          <button
            type="button"
            class="bg-surface-canvas dark:bg-surface-level-800 dark:hover:text-content-level-300 focus:outline-none hover:text-content-level-500 p-1 rounded-md text-content-level-400"
            @click="closeLocationsModal"
          >
            <span class="sr-only">
              {{ $t('commerce.storeSlug.close') }}
            </span>

            <X class="h-5 w-5" />
          </button>
        </div>

        <div class="max-h-[60vh] overflow-y-auto">
          <button
            v-for="location in otherLocations"
            :key="location.slug"
            type="button"
            class="border-b border-border-level-100 cursor-pointer dark:border-border-level-600 dark:hover:bg-surface-canvas/5 hover:bg-surface-level-50 last:border-b-0 px-6 py-4 text-left w-full"
            @click="() => goToLocation(location.slug)"
          >
            <div class="dark:text-content-inverse font-medium text-content-level-900 text-sm">
              {{ formatStoreAddress(location) }}
            </div>

            <div class="dark:text-content-level-400 mt-1 text-content-level-500 text-xs">
              {{
                $t(
                  'commerce.storeSlug.distanceMilesAway',
                  { distance: location.distance_miles },
                  Number(location.distance_miles)
                )
              }}
            </div>
          </button>

          <button
            v-if="viewAllHref"
            type="button"
            class="border-border-level-200 border-t cursor-pointer dark:border-border-level-600 flex items-center justify-between px-6 py-4 text-left text-primary w-full"
            :aria-label="
              $t('commerce.storeSlug.viewAllLocationsLabel', {
                brandName: locationsData?.brand.name ?? ''
              })
            "
            @click="onViewAll"
          >
            <span class="font-medium text-sm">
              {{ $t('commerce.storeSlug.viewAll') }}
            </span>

            <ChevronRightIcon class="h-4 w-4" />
          </button>
        </div>
      </template>
    </BaseModal>
  </div>
</template>

<script lang="ts">
import { navigateTo } from '#app'
import type { DropdownMenuItem, DropdownMenuProps } from '@nuxt/ui'
import { UDropdownMenu } from '#components'
import {
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  InformationCircleIcon
} from '@heroicons/vue/24/outline'
import { Clock, Heart, Search, SparklesIcon, Utensils, X } from '@lucide/vue'
import { DateTime } from 'luxon'
import { computed, onScopeDispose, watch } from 'vue'
import { useRoute } from 'vue-router'
import AvailabilityErrorModal from '~/components/AvailabilityErrorModal.vue'
import { InputSelect } from '~/components/forms/inputs'
import MenuItemModal from '~/components/MenuItemModal.vue'
import MobileEventDetailsButton from '~/components/MobileEventDetailsButton.vue'
import MobileEventDetailsModal from '~/components/MobileEventDetailsModal.vue'
import StoreInfoModal from '~/components/StoreInfoModal.vue'
import BaseModal from '~/components/shared/dialogs/BaseModal.vue'
import AppErrorState from '~/components/shared/AppErrorState.vue'
import EmptyState from '~/components/shared/EmptyState.vue'
import StoreReviews from '~/components/store/StoreReviews.vue'
import StoreReviewsSlider from '~/components/store/StoreReviewsSlider.vue'
import { useGuestCountPrompt } from '~/composables/useGuestCountPrompt'
import { useMenuItemPriceTypeCopy } from '~/composables/useMenuItemPriceTypeCopy'
import { useLogger } from '~/composables/useLogger'
import { useCurrencyFormat } from '~/composables/useCurrencyFormat'
import { useMenuDescriptionFormat } from '~/composables/useMenuDescriptionFormat'
import type { AvailabilityCode } from '~/composables/AvailabilityCode'
import { waitForStoreContext } from '~/composables/waitForStoreContext'
import { parseSelectedRequestDate } from '~/composables/parseSelectedRequestDate'
import { usePresentationTranslator } from '~/composables/usePresentationTranslator'
import { CarouselDirection, type DietaryOptionType } from '~/enums'
import { isOrderType } from '~/enums/order/isOrderType'
import { OrderType } from '~/enums/order/OrderType'
import { formatStoreAddress } from '~/utils/formatStoreAddress'
import {
  type CartItemView,
  type DayOfWeek,
  DayOfWeekSchema,
  type MenuItemPriceType,
  type PublicMenuDto,
  type PublicStoreDto,
  type GoogleReviewEntry,
  type StoreReview,
  type StoreReviewStatsDto
} from '~/services/api/generated'
import { cartCustomizationsToModalFormat } from '~/utils/cartCustomizationsToModalFormat'
import { hasStoreReviews } from '~/utils/hasStoreReviews'
import { indexMenuItemsById } from '~/utils/indexMenuItemsById'
import { scrollCarouselStep } from '~/utils/scrollCarouselStep'
import { Normalization } from '~/utils/normalization'
import type { TimeSlotStore } from '~/utils/TimeSlotStore'
import { OrderTypePresentation } from '~/presentation/OrderTypePresentation'
import type { Customization } from '~/types/Customization'
import { useStorefrontApplication } from '~/composables/useStorefrontApplication'
type StoreMenuType = 'dine-in' | 'catering'
interface StoreOrderTypeContext {
  menuType: StoreMenuType
  orderType: OrderType
  serviceType: 'pickup' | 'dine_in' | null
}
const getDefaultMenuTypeForOrderType = (orderType: OrderType): StoreMenuType =>
  orderType === 'dine_in' ? 'dine-in' : 'catering'
export const StorePageOrderType = {
  fromCanonical(orderType: OrderType): StoreOrderTypeContext {
    return {
      menuType: getDefaultMenuTypeForOrderType(orderType),
      orderType,
      serviceType: orderType === 'delivery' ? null : orderType
    }
  },
  resolve(value: unknown): StoreOrderTypeContext | null {
    if (!isOrderType(value)) return null
    return StorePageOrderType.fromCanonical(value)
  }
}
</script>

<script setup lang="ts">
const logger = useLogger('StorePage')
const initializationController = new AbortController()
onScopeDispose(() => {
  initializationController.abort()
})
const { identity } = useIdentity()
const { showAddToCart } = useShowAddToCart()
const { toText: formatMenuDescriptionText } = useMenuDescriptionFormat()
const priceTypeCopy = useMenuItemPriceTypeCopy()
const t = usePresentationTranslator()
const formatCurrency = useCurrencyFormat()
const { t: translatePlural } = useI18n()
definePageMeta({
  layout: 'search',
  sanctum: {
    excluded: true
  }
})
// Types
interface MenuCategory {
  id: number
  name: string
  description: string | null
  status: string
  sort_order: number | null
  is_featured_category?: boolean
  menu_items: MenuItem[]
}
interface Menu {
  id: number
  name: string
  description: string | null
  status: string
  availability: string | null
  availability_mode: string
  availability_code?: AvailabilityCode
  is_available: boolean
  sync_with_store_hours: boolean
  type: string
  order_types: string[]
  types: PublicMenuDto['types']
  tags: PublicMenuDto['tags']
  schedule: MenuScheduleItem[]
  custom_hours: MenuScheduleItem[]
  sort_order: number
  categories: MenuCategory[]
}
interface MenuScheduleItem {
  day_of_week: string
  start_time: string | null
  end_time: string | null
  is_enabled: boolean
}
interface MenuItem {
  id: number
  name: string
  description: string | null
  price: string
  price_type?: MenuItemPriceType
  image?: string
  is_featured: boolean
  preparation_time: number | null
  sort_order: number
  serves_count: string | null
  calories_per_serving: number | null
  popularity_score: number | null
  order_count: number
  is_popular: boolean
  is_special: boolean
  is_new: boolean
  individual_packaging_available?: boolean
  dietary_options: DietaryOption[]
  customizations?: Customization[]
  created_at: string | null
  updated_at: string | null
}
interface DietaryOption {
  id: number
  name: string
  slug: string
  type: DietaryOptionType
}
interface FilteredMenuItem extends MenuItem {
  categoryName: string
  categoryId: number
}
type DietaryIconName =
  | 'gluten-free'
  | 'dairy-free'
  | 'nut-free'
  | 'vegetarian'
  | 'vegan'
  | 'low-carb'
  | 'keto'
  | 'halal'
  | 'kosher'
const dietaryIconNames: DietaryIconName[] = [
  'gluten-free',
  'dairy-free',
  'nut-free',
  'vegetarian',
  'vegan',
  'low-carb',
  'keto',
  'halal',
  'kosher'
]
const toAvailabilityErrorData = (
  data: NonNullable<ReturnType<typeof useStorefrontApplication>['availabilityErrorData']['value']>
) => {
  if (data.reason === undefined) return null
  return {
    ...(data.address === undefined ? {} : { address: data.address }),
    available: data.available,
    ...(data.requested_date === undefined || data.requested_date === null
      ? {}
      : { requested_date: data.requested_date }),
    ...(data.distance === undefined ? {} : { distance: Number(data.distance) }),
    ...(data.operating_hours === undefined ? {} : { operating_hours: data.operating_hours }),
    order_type: data.order_type,
    reason: data.reason,
    store_id: Number(data.store_id),
    store_name: data.store_name,
    ...(data.requested_time === undefined || data.requested_time === null
      ? {}
      : { requested_time: data.requested_time }),
    ...(data.current_time === undefined || data.current_time === null
      ? {}
      : { current_time: data.current_time })
  }
}
const getRouteQueryString = (value: unknown): string | undefined =>
  typeof value === 'string' ? value : undefined
// Route and state
const route = useRoute()
const slug = computed(() => Normalization.trimmedStringValue(route.params['slug'], '') ?? '')
// Check if we came from an event page or search page
const eventSlug = computed(() => getRouteQueryString(route.query['eventSlug']))
const fromSource = computed(() => getRouteQueryString(route.query['from']))
// Computed properties for back button
const backButtonVisible = computed(() => {
  // Only show if we have a specific source
  return (eventSlug.value != null && eventSlug.value !== '') || fromSource.value === 'search'
})
const backButtonText = computed(() => {
  return 'Back' // Fallback (shouldn't show if visible is false)
})
const storeOrderTypes = computed<string[]>(() => store.value?.order_types ?? [])
const storeImageAttrs = computed(() => ({ alt: store.value?.name ?? 'Store' }))
const menuItemImageAttrs = (name: string) => ({ alt: name })
const storeForSearchNavigation = computed<TimeSlotStore | undefined>(() => {
  if (!store.value) return undefined
  const result: TimeSlotStore = { timezone: store.value.timezone ?? null }
  if (store.value.lead_time_type !== undefined) result.lead_time_type = store.value.lead_time_type
  if (store.value.lead_time_hours !== undefined)
    result.lead_time_hours = store.value.lead_time_hours
  if (store.value.day_before_cutoff_time !== undefined) {
    result.day_before_cutoff_time = store.value.day_before_cutoff_time
  }
  if (store.value.multiple_days_notice !== undefined) {
    result.multiple_days_notice = store.value.multiple_days_notice
  }
  if (store.value.operating_hours != null) {
    result.operating_hours = store.value.operating_hours.map((hours) => ({ ...hours }))
  }
  return result
})
const storefront = useStorefrontApplication({
  defaultClosedMessage: () => t('public.storeSlug.temporarilyUnavailableMessage'),
  onCloseMenuItemModal: () => {
    closeMenuItemModal()
  },
  signal: initializationController.signal,
  slug,
  t
})
const {
  store,
  timezone: storeTimezone,
  eventDate: selectedRequestDateForApi,
  loading,
  error,
  availabilityResult: storeAvailabilityResult,
  isFullPageBlockVisible,
  closureMessage: temporarilyClosedStoreMessage,
  canOrder,
  showAvailabilityErrorModal,
  availabilityErrorMessage,
  availabilityErrorData: rawAvailabilityErrorData,
  showAvailabilityBlock,
  closeAvailabilityBlock,
  getFirstAvailabilityReason,
  loadStoreContext: fetchStoreDetails,
  refreshAvailability,
  selectedMenuId,
  selectedMenuType,
  menusLoading,
  hasLoadedMenus,
  menusError,
  fetchStoreMenus,
  fetchLocations,
  locationsData,
  selection: selectedOrderContext,
  orderDetailsMissingReason,
  restoreContext,
  setOrderType,
  hasMenuDateValidationContext,
  selectedMenuUnavailableReason,
  isAddingToCart,
  addToCart,
  checkCartAvailability
} = storefront
const menus = storefront.menus
const isSaved = ref(false)
const activeCategory = ref<number | null>(null)
const isScrollingToCategory = ref(false)
let programmaticScrollTimer: ReturnType<typeof setTimeout> | null = null
let scrollSpyRaf: number | null = null
const showLocationsModal = ref(false)
const showStoreInfoModal = ref(false)
const storeReviewsRef = useTemplateRef<{
  reviews: StoreReview[]
  googleReviews: GoogleReviewEntry[]
  reviewsStats: StoreReviewStatsDto | null
  googleStats: StoreReviewStatsDto | null
}>('storeReviewsRef')
const storeReviewsSliderProps = computed(() => {
  const props: {
    totalRatings?: number
    googleTotalRatings?: number
  } = {}
  const totalReviews = storeReviewsRef.value?.reviewsStats?.total_reviews
  if (totalReviews !== undefined) props.totalRatings = totalReviews
  const googleTotalReviews = storeReviewsRef.value?.googleStats?.total_reviews
  if (googleTotalReviews !== undefined) props.googleTotalRatings = googleTotalReviews
  return props
})
const hasOtherLocations = computed(() => (locationsData.value?.total_stores ?? 0) > 0)
const otherLocations = computed(() => locationsData.value?.stores ?? [])
const otherLocationsCount = computed(() => locationsData.value?.total_stores ?? 0)
const showMenuItemModal = ref(false)
const selectedMenuItem = ref<MenuItem | null>(null)
const editingCartItem = ref<CartItemView | null>(null)
const menuItemEditMode = ref(false)
const menuItemInitialQuantity = ref<number | undefined>(undefined)
const menuItemInitialCustomizations = ref<Record<number, Record<number, number>> | undefined>(
  undefined
)
const menuItemInitialInstructions = ref<string | undefined>(undefined)
const menuItemModalProps = computed(() => {
  const props: {
    initialQuantity?: number
    initialCustomizations?: Record<number, Record<number, number>>
    initialSpecialInstructions?: string
  } = {}
  if (menuItemInitialQuantity.value !== undefined)
    props.initialQuantity = menuItemInitialQuantity.value
  if (menuItemInitialCustomizations.value !== undefined) {
    props.initialCustomizations = menuItemInitialCustomizations.value
  }
  if (menuItemInitialInstructions.value !== undefined) {
    props.initialSpecialInstructions = menuItemInitialInstructions.value
  }
  return props
})
const availabilityErrorProps = computed(() => {
  const props: {
    errorMessage?: string
  } = {}
  if (availabilityErrorMessage.value !== undefined && availabilityErrorMessage.value !== null) {
    props.errorMessage = availabilityErrorMessage.value
  }
  return props
})
const showMobileEventDetailsModal = ref(false)
const menuSearchQuery = ref('')
const expandedMenuIds = ref<Set<string>>(new Set())
const searchNavigationRef = useTemplateRef<{
  openDateTimeFilter?: () => void
  openGuestCountPrompt?: () => void
}>('searchNavigationRef')
const failedMenuItemImages = ref<Set<number>>(new Set())
// Check if a menu item image should be displayed (exists and hasn't failed to load)
const shouldShowMenuItemImage = (item: MenuItem | null | undefined): boolean => {
  if (!item) return false
  const imageUrl = getMenuItemImage(item)
  const hasUrl = imageUrl != null && imageUrl !== ''
  const hasNotFailed = !failedMenuItemImages.value.has(item.id)
  return hasUrl && hasNotFailed
}
// Mark a menu item image as failed to load
const markMenuItemImageAsFailed = (itemId: number) => {
  failedMenuItemImages.value.add(itemId)
}
const temporaryClosurePrimaryAction = computed(() => ({
  icon: Search,
  label: 'Back to Search',
  onClick: () => navigateTo('/search')
}))
const canonicalDayNames: DayOfWeek[] = DayOfWeekSchema.options
const dayNamesByIndex = canonicalDayNames.slice(-1).concat(canonicalDayNames.slice(0, -1))
const normalizeDayValue = (value: unknown) => {
  const raw = String(value ?? '')
    .trim()
    .toLowerCase()
  if (/^\d+$/.test(raw)) return dayNamesByIndex[Number(raw)] ?? raw
  return raw
}
const isScheduleDayEnabled = (item: MenuScheduleItem) => {
  return item.is_enabled
}
const storeAddress = computed(() =>
  [store.value?.address, store.value?.city, store.value?.state, store.value?.zip]
    .filter((value) => value != null && value !== '')
    .join(', ')
)
const hasReviews = computed<boolean>(
  () => store.value !== null && hasStoreReviews(store.value.review_count)
)
const getSelectedRequestDateFromApiValue = () => {
  const value = selectedRequestDateForApi.value
  if (!value) return null
  const asIsoDate = DateTime.fromISO(value, { zone: storeTimezone.value })
  return asIsoDate.isValid ? asIsoDate : null
}
const UNAVAILABLE_SELECTED_DATE_MESSAGE = 'Unavailable on Selected Date'
const UNAVAILABLE_SELECTED_TIME_MESSAGE = 'Unavailable at Selected Time'
const normalizeTimeValue = (value: unknown) => {
  const raw = String(value ?? '').trim()
  if (!raw) return ''
  if (raw.toLowerCase() === 'noon') return '12:00'
  if (raw.toLowerCase() === 'midnight') return '00:00'
  const twentyFourHour = /^(\d{1,2}):(\d{2})(?::\d{2})?$/.exec(raw)
  if (twentyFourHour) {
    return `${twentyFourHour[1]!.padStart(2, '0')}:${twentyFourHour[2]}`
  }
  const meridiem = /^(\d{1,2})(?::(\d{2}))?\s*(am|pm)$/i.exec(raw)
  if (!meridiem) return ''
  let hour = Number(meridiem[1])
  const minute = meridiem[2] ?? '00'
  const suffix = meridiem[3]!.toLowerCase()
  if (suffix === 'pm' && hour < 12) hour += 12
  if (suffix === 'am' && hour === 12) hour = 0
  return `${String(hour).padStart(2, '0')}:${minute}`
}
const selectedRequestDay = computed(() => {
  const parsed = parseSelectedRequestDate(selectedOrderContext.value.eventDate, storeTimezone.value)
  if (parsed?.isValid === true) return parsed.toFormat('cccc').toLowerCase()
  const apiDate = getSelectedRequestDateFromApiValue()
  return apiDate?.isValid === true ? apiDate.toFormat('cccc').toLowerCase() : ''
})
const currentOrSelectedRequestDay = computed(() => {
  if (selectedRequestDay.value !== '') return selectedRequestDay.value
  return DateTime.now().setZone(storeTimezone.value).toFormat('cccc').toLowerCase()
})
const getMenuSchedule = (menu: Menu): MenuScheduleItem[] => {
  return menu.custom_hours.length > 0 ? menu.custom_hours : menu.schedule
}
const menuHasExplicitSchedule = (menu: Menu) =>
  menu.custom_hours.length > 0 || menu.schedule.length > 0
const formatDayLabel = (value: string) => value.charAt(0).toUpperCase() + value.slice(1)
const formatTimeLabel = (value: unknown) => {
  const normalized = normalizeTimeValue(value)
  if (!normalized) return ''
  const parsed = DateTime.fromFormat(normalized, 'HH:mm', { zone: 'UTC' })
  return parsed.isValid ? parsed.toFormat('h:mm a').toUpperCase() : normalized
}
const formatTimeRangeLabel = (startTime: unknown, endTime: unknown) => {
  const start = formatTimeLabel(startTime)
  const end = formatTimeLabel(endTime)
  if (!start || !end) return ''
  return `${start}-${end}`
}
interface NormalizedScheduleEntry {
  day: string
  startTime?: string
  endTime?: string
}
const getEnabledMenuScheduleEntries = (menu: Menu): NormalizedScheduleEntry[] =>
  getMenuSchedule(menu)
    .filter((item) => isScheduleDayEnabled(item))
    .map((item) => ({
      day: normalizeDayValue(item.day_of_week),
      ...(item.start_time === null ? {} : { startTime: item.start_time }),
      ...(item.end_time === null ? {} : { endTime: item.end_time })
    }))
    .filter((item) => item.day)
const getStoreOperatingHoursEntry = (day: string) => {
  const storeHours = Array.isArray(store.value?.operating_hours) ? store.value.operating_hours : []
  return storeHours.find((item) => normalizeDayValue(item.day_of_week) === day && item.is_open)
}
const findNextScheduleEntry = (entries: NormalizedScheduleEntry[], day: string) => {
  if (entries.length === 0) return null
  const dayIndex = dayNamesByIndex.findIndex((candidate) => candidate === day)
  if (dayIndex === -1) return entries[0] ?? null
  const firstEntryByDay = new Map<string, NormalizedScheduleEntry>()
  for (const entry of entries) {
    if (!firstEntryByDay.has(entry.day)) firstEntryByDay.set(entry.day, entry)
  }
  for (let offset = 1; offset <= dayNamesByIndex.length; offset += 1) {
    const nextDay = dayNamesByIndex[(dayIndex + offset) % dayNamesByIndex.length]
    const entry = nextDay === undefined ? undefined : firstEntryByDay.get(nextDay)
    if (entry) return entry
  }
  return entries[0] ?? null
}
const getMenuAvailabilityWindowLabel = (menu: Menu) => {
  if (!menuUsesCustomAvailability(menu)) {
    const day = currentOrSelectedRequestDay.value
    const storeHours = getStoreOperatingHoursEntry(day)
    const range = formatTimeRangeLabel(storeHours?.open_time, storeHours?.close_time)
    if (!range) return 'Available during store hours'
    return selectedRequestDay.value
      ? `Available ${formatDayLabel(day)} ${range}`
      : `Available today ${range}`
  }
  const entries = getEnabledMenuScheduleEntries(menu)
  if (entries.length === 0) return ''
  const uniqueDays = new Set(entries.map((item) => item.day))
  const uniqueRanges = new Set(
    entries.map((item) => formatTimeRangeLabel(item.startTime, item.endTime)).filter(Boolean)
  )
  if (uniqueDays.size === 7 && uniqueRanges.size === 1) {
    return `Available daily ${Array.from(uniqueRanges)[0]}`
  }
  const day = currentOrSelectedRequestDay.value
  const dayEntry = entries.find((item) => item.day === day)
  if (dayEntry) {
    const range = formatTimeRangeLabel(dayEntry.startTime, dayEntry.endTime)
    if (!range) return ''
    return selectedRequestDay.value
      ? `Available ${formatDayLabel(day)} ${range}`
      : `Available today ${range}`
  }
  if (selectedRequestDay.value) {
    return ''
  }
  if (hasMenuDateValidationContext.value) {
    return ''
  }
  const nextEntry = findNextScheduleEntry(entries, day)
  if (!nextEntry) return ''
  const range = formatTimeRangeLabel(nextEntry.startTime, nextEntry.endTime)
  if (!range) return ''
  return `Available ${formatDayLabel(nextEntry.day)} ${range}`
}
const menuUsesCustomAvailability = (menu: Menu) => {
  const mode = menu.availability_mode.toLowerCase()
  if (mode === 'sync' || menu.sync_with_store_hours) return false
  if (mode === 'custom' || !menu.sync_with_store_hours) return true
  if (menuHasExplicitSchedule(menu)) return true
  return false
}
const getMenuUnavailableReason = (menu: Menu | null | undefined): string =>
  storefront.getMenuUnavailableReason(
    menu == null ? null : storefront.menus.value.find((rawMenu) => rawMenu.id === menu.id)
  )
const selectedMenuAvailabilityWindowLabel = computed(() => {
  if (!selectedMenu.value || !selectedMenuUnavailableReason.value) return ''
  if (
    selectedMenuUnavailableReason.value === 'Not available for selected order type' ||
    selectedMenuUnavailableReason.value === UNAVAILABLE_SELECTED_DATE_MESSAGE ||
    selectedMenuUnavailableReason.value === UNAVAILABLE_SELECTED_TIME_MESSAGE
  ) {
    return selectedMenuUnavailableReason.value
  }
  return getMenuAvailabilityWindowLabel(selectedMenu.value)
})
const getMenuAvailabilityStatusLine = (menu: Menu | null | undefined) => {
  const reason = getMenuUnavailableReason(menu)
  if (!menu || !reason) return ''
  if (
    reason === 'Not available for selected order type' ||
    reason === UNAVAILABLE_SELECTED_DATE_MESSAGE ||
    reason === UNAVAILABLE_SELECTED_TIME_MESSAGE
  ) {
    return reason
  }
  return getMenuAvailabilityWindowLabel(menu)
}
// The cart drawer emits when its own isStoreUnavailable pre-check fails (no access to this
// page's modal); every other failure lands in useStoreOrderingContext's own canonical state.
const handleCartAvailabilityFailed = (payload: { message: () => string; data?: unknown }) => {
  showAvailabilityBlock(payload.message)
}
// Filter state
const filters = ref<{
  individualPackaging: boolean
  dietaryOptions: string[]
}>({
  dietaryOptions: [],
  individualPackaging: false
})
// Built up from what the store accepts (already post-dine-in-override), so a
// tab it cannot serve is never constructed.
const menuTypeOptions = computed<
  {
    value: StoreMenuType
    label: string
  }[]
>(() => {
  const orderTypes = storeOrderTypes.value
  const options: {
    value: StoreMenuType
    label: string
  }[] = []
  if (orderTypes.includes(OrderType.DINE_IN)) {
    options.push({ label: OrderTypePresentation[OrderType.DINE_IN].label, value: 'dine-in' })
  }
  if (orderTypes.includes(OrderType.PICKUP) || orderTypes.includes(OrderType.DELIVERY)) {
    options.push({ label: 'Catering', value: 'catering' })
  }
  return options
})
// Catering resolves to pickup when the store supports it, delivery otherwise.
const getOrderTypeForMenuType = (menuType: StoreMenuType): OrderType => {
  if (menuType === 'dine-in') return OrderType.DINE_IN
  return storeOrderTypes.value.includes(OrderType.PICKUP) ? OrderType.PICKUP : OrderType.DELIVERY
}
// Composables
const { toggleFavorite: toggleFavoriteComposable, isFavorite } = useFavorites()
const hasMenus = computed(() => menus.value.length > 0)
const hasMultipleMenus = computed(() => menus.value.length > 1)
const selectedMenuTypeLabel = computed(
  () =>
    menuTypeOptions.value.find((option) => option.value === selectedMenuType.value)?.label ??
    selectedMenuType.value ??
    ''
)
// No order types means no label to interpolate; a store closure is handled
// separately in onMounted and never reaches this branch.
const emptyMenusTitle = computed(() =>
  menuTypeOptions.value.length === 0
    ? t('commerce.storeSlug.noServiceAvailable')
    : t('commerce.storeSlug.noLabelMenusAvailable', { label: selectedMenuTypeLabel.value })
)
const selectedMenu = storefront.selectedMenu
const menuCategories = computed(() => {
  return selectedMenu.value?.categories ?? []
})
const filteredMenuItems = computed(() => {
  if (!selectedMenu.value) return []
  let allItems: FilteredMenuItem[] = []
  selectedMenu.value.categories.forEach((category) => {
    category.menu_items.forEach((item) => {
      allItems.push({
        ...item,
        categoryId: category.id,
        categoryName: category.name
      })
    })
  })
  // Apply filters
  if (filters.value.individualPackaging) {
    allItems = allItems.filter((item) => item.individual_packaging_available === true)
  }
  if (selectedDietaryOptions.value.length > 0) {
    allItems = allItems.filter((item) =>
      item.dietary_options.some((diet: DietaryOption) =>
        selectedDietaryOptions.value.includes(diet.slug)
      )
    )
  }
  return allItems
})
const visibleMenuCategories = computed(() =>
  menuCategories.value.filter((category) =>
    filteredMenuItems.value.some((item) => item.categoryId === category.id)
  )
)
const isFeaturedCategory = (category: MenuCategory) => category.is_featured_category === true
const featuredVisibleCategories = computed(() =>
  visibleMenuCategories.value.filter(isFeaturedCategory)
)
const regularVisibleCategories = computed(() =>
  visibleMenuCategories.value.filter((category) => !isFeaturedCategory(category))
)
// Computed property for store categories
const storeCategories = computed(() => {
  if (store.value?.categories == null) return []
  const categories: NonNullable<PublicStoreDto['categories']> = []
  const seenNames = new Set<string>()
  for (const category of store.value.categories) {
    if (category.name !== '' && !seenNames.has(category.name)) {
      categories.push(category)
      seenNames.add(category.name)
    }
  }
  return categories
})
const availableDietaryOptions = computed(() => {
  const allOptions = new Set<string>()
  selectedMenu.value?.categories.forEach((category) => {
    category.menu_items.forEach((item) => {
      item.dietary_options.forEach((diet: DietaryOption) => {
        allOptions.add(diet.slug)
      })
    })
  })
  return Array.from(allOptions).map((slug) => {
    const option = {
      badge: getDietaryBadge(slug),
      badgeClass: getDietaryBadgeClass(slug),
      icon: getDietaryIconName(slug),
      name: slug
        .split('-')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' '),
      slug
    }
    return option
  })
})
const selectedDietaryOptions = computed(() =>
  filters.value.dietaryOptions.filter((slug) =>
    availableDietaryOptions.value.some((option) => option.slug === slug)
  )
)
const isFilterActive = computed(() => {
  return filters.value.individualPackaging || selectedDietaryOptions.value.length > 0
})
useHead({
  title: computed(() =>
    store.value?.name != null && store.value.name !== ''
      ? `${store.value.name} | Mealz`
      : 'Catering | Mealz'
  )
})
// Combines the feature flag with the same closure projection the full-page block uses, so
// template gating and the full-page block can never disagree.
const canInteractWithMenuItems = computed(
  () => showAddToCart.value && !isFullPageBlockVisible.value
)
const availabilityErrorData = computed(() =>
  rawAvailabilityErrorData.value === null
    ? null
    : toAvailabilityErrorData(rawAvailabilityErrorData.value)
)
const cartUnavailableMessage = computed(
  () => getFirstAvailabilityReason() || temporarilyClosedStoreMessage.value
)
// Runs after every successful store load (initial mount, composable's own refetch, or "Try
// Again"), all updating this same shared ref.
watch(store, (value) => {
  if (value === null) return
  if (identity.value != null) {
    // Authenticated user: check from server
    isSaved.value = !!value.is_favorite
  } else {
    // Guest user: check from cookie
    isSaved.value = isFavorite(value.id)
  }
})
const getDietaryIconName = (slug: string): DietaryIconName => {
  // Map API slugs to DietaryIcon component names
  return dietaryIconNames.find((name) => name === slug) ?? 'vegetarian'
}
const toggleSave = async () => {
  const storeToToggle = store.value
  if (!storeToToggle) return
  const prev = isSaved.value
  const next = !prev
  isSaved.value = next
  try {
    const success = await toggleFavoriteComposable(storeToToggle)
    if (!success) {
      isSaved.value = prev
      logger.error('toggleSave')
    }
  } catch (error) {
    isSaved.value = prev
    logger.error('toggleSave', { error })
  }
}
const preloadMenuItemModalImage = useMenuItemModalImagePreload()
const openMenuItemModal = (item: MenuItem, event?: Event) => {
  if (!showAddToCart.value) return
  if (isFullPageBlockVisible.value) {
    showAvailabilityBlock(() => temporarilyClosedStoreMessage.value)
    return
  }
  if (selectedMenuUnavailableReason.value) {
    showAvailabilityBlock(
      () => selectedMenuUnavailableReason.value,
      storeAvailabilityResult.value?.data ?? null
    )
    return
  }
  if (event) {
    event.preventDefault()
    event.stopPropagation()
  }
  preloadMenuItemModalImage(item)
  selectedMenuItem.value = item
  showMenuItemModal.value = true
}
const menuItemsById = computed(() => indexMenuItemsById(menus.value))
const findMenuItemById = (menuItemId: number): MenuItem | null =>
  menuItemsById.value.get(menuItemId) ?? null
const openEditCartItemModal = (cartItem: CartItemView) => {
  const menuItem = findMenuItemById(cartItem.menu_item_id)
  if (!menuItem) {
    logger.error('openEditCartItemModal', { menuItemId: cartItem.menu_item_id })
    return
  }
  editingCartItem.value = cartItem
  menuItemEditMode.value = true
  menuItemInitialQuantity.value = cartItem.quantity
  menuItemInitialCustomizations.value = cartCustomizationsToModalFormat(cartItem.customizations)
  menuItemInitialInstructions.value = cartItem.special_instructions ?? ''
  selectedMenuItem.value = menuItem
  showMenuItemModal.value = true
}
const closeMenuItemModal = () => {
  showMenuItemModal.value = false
  selectedMenuItem.value = null
  editingCartItem.value = null
  menuItemEditMode.value = false
  menuItemInitialQuantity.value = undefined
  menuItemInitialCustomizations.value = undefined
  menuItemInitialInstructions.value = undefined
}
const openOrderDetailsModal = () => {
  closeMenuItemModal()
  if (import.meta.client && window.matchMedia('(max-width: 1023px)').matches) {
    showMobileEventDetailsModal.value = true
    return
  }
  searchNavigationRef.value?.openDateTimeFilter?.()
}
const openMobileEventDetails = () => {
  showMobileEventDetailsModal.value = true
}
// A Group Dining visitor with no party size is asked for it before any availability call.
const { openGuestCountPrompt } = useGuestCountPrompt({
  needsGuestCount: () => selectedOrderContext.value.needsGuestCount,
  openMobilePanel: openMobileEventDetails,
  openNavigationPrompt: () => searchNavigationRef.value?.openGuestCountPrompt?.()
})
const closeMobileEventDetails = () => {
  showMobileEventDetailsModal.value = false
}
const openStoreInfoModal = () => {
  showStoreInfoModal.value = true
}
const closeStoreInfoModal = () => {
  showStoreInfoModal.value = false
}
const openLocationsModal = () => {
  showLocationsModal.value = true
}
const closeLocationsModal = () => {
  showLocationsModal.value = false
}
const closeAvailabilityError = () => {
  closeAvailabilityBlock()
}
const clearMenuSearch = () => {
  menuSearchQuery.value = ''
}
const handleMobileEventDetailsUpdate = (eventDetails: unknown) => {
  // Handle mobile event details update
  // This would typically update the search store and trigger a search refresh
  logger.debug('handleMobileEventDetailsUpdate', { eventDetails })
  showMobileEventDetailsModal.value = false
}
const handleMenuItemModalResult = async (
  cartItem: Parameters<ReturnType<typeof useStorefrontApplication>['addToCart']>[0]
) => {
  if (isAddingToCart.value) return
  if (editingCartItem.value) {
    // Edit mode - update existing item
    await storefront.updateCartItem(editingCartItem.value.id, cartItem)
  } else {
    // Add mode - create new item
    await addToCart(cartItem)
  }
}
const goBack = async (): Promise<void> => {
  // Only navigate if we have specific parameters
  if (eventSlug.value != null && eventSlug.value !== '') {
    await navigateTo(`/event/${eventSlug.value}`)
  } else if (fromSource.value === 'search') {
    await navigateTo('/search')
  }
}
const viewAllHref = computed<string | null>(() =>
  locationsData.value === null ? null : `/brands/${locationsData.value.brand.slug}`
)
const onViewAll = async (): Promise<void> => {
  if (viewAllHref.value == null || viewAllHref.value === '') return
  showLocationsModal.value = false
  await navigateTo(viewAllHref.value)
}
const goToLocation = async (locationSlug: string): Promise<void> => {
  showLocationsModal.value = false
  await navigateTo(`/store/${locationSlug}`)
}
const locationMenuContent: DropdownMenuProps['content'] = { align: 'center' }
const locationMenuUi: DropdownMenuProps['ui'] = { content: 'w-80' }
const otherLocationMenuItems = computed<DropdownMenuItem[][]>(() => [
  [
    {
      label: t('commerce.storeSlug.countLocations', { count: otherLocationsCount.value }),
      type: 'label'
    },
    ...otherLocations.value.map((location): DropdownMenuItem => ({
      description: translatePlural(
        'commerce.storeSlug.distanceMilesAway',
        { distance: location.distance_miles },
        Number(location.distance_miles)
      ),
      label: formatStoreAddress(location),
      onSelect: () => {
        void goToLocation(location.slug)
      }
    }))
  ],
  ...(viewAllHref.value === null
    ? []
    : [
        [
          {
            label: t('commerce.storeSlug.viewAll'),
            onSelect: () => {
              void onViewAll()
            },
            slot: 'view-all'
          }
        ]
      ])
])
const getDietaryBadge = (slug: string): string => {
  const badgeMap: Record<string, string> = {
    'dairy-free': 'DF',
    'gluten-free': 'GF',
    halal: 'H',
    keto: 'K',
    kosher: 'K',
    'low-carb': 'LC',
    'nut-free': 'NF',
    vegan: 'VG',
    vegetarian: 'V'
  }
  return badgeMap[slug] ?? slug.charAt(0).toUpperCase()
}
const getDietaryBadgeClass = (slug: string): string => {
  const classMap: Record<string, string> = {
    'dairy-free': 'border border-border-level-300 text-content-level-900 bg-surface-canvas',
    'gluten-free': 'border border-border-level-300 text-content-level-900 bg-surface-canvas',
    halal: 'bg-success-600 text-content-inverse',
    keto: 'bg-badge-dietary-keto text-content-inverse',
    kosher: 'bg-info-600 text-content-inverse',
    'low-carb': 'bg-info-600 text-content-inverse',
    'nut-free': 'border border-border-level-300 text-content-level-900 bg-surface-canvas',
    vegan: 'bg-success-600 text-content-inverse',
    vegetarian: 'bg-success-600 text-content-inverse'
  }
  return classMap[slug] ?? 'bg-surface-level-600 text-content-inverse'
}
const clearFilters = () => {
  filters.value.individualPackaging = false
  filters.value.dietaryOptions = []
}
const scrollToCategory = (categoryId: number) => {
  isScrollingToCategory.value = true
  activeCategory.value = categoryId
  const element = document.getElementById(`category-${categoryId}`)
  if (element) {
    element.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }
  if (programmaticScrollTimer) clearTimeout(programmaticScrollTimer)
  programmaticScrollTimer = setTimeout(() => {
    isScrollingToCategory.value = false
    updateActiveCategoryFromViewport()
  }, 800)
}
const onMenuTypeChange = (type: StoreMenuType) => {
  if (selectedMenuType.value === type || menusLoading.value) return
  selectedMenuType.value = type
  setOrderType(getOrderTypeForMenuType(type))
  selectedMenuId.value = null
  expandedMenuIds.value = new Set()
  menuSearchQuery.value = ''
  clearFilters()
}
const supportsMenuType = (menuType: StoreMenuType): boolean =>
  menuTypeOptions.value.some((option) => option.value === menuType)
// Corrects a pre-fetch tab guess to the store's first offered tab, and keeps
// searchStore.orderType in step since fetchStoreMenus() gates on that field.
const reconcileMenuTypeAfterStoreLoad = () => {
  if (selectedMenuType.value !== null && supportsMenuType(selectedMenuType.value)) return
  selectedMenuType.value = menuTypeOptions.value[0]?.value ?? null
  if (selectedMenuType.value !== null) {
    setOrderType(getOrderTypeForMenuType(selectedMenuType.value))
  }
}
const syncMenuTypeWithOrderType = (orderType: OrderType) => {
  const nextMenuType = StorePageOrderType.fromCanonical(orderType).menuType
  if (selectedMenuType.value === nextMenuType) return
  // An unsupported pick is reverted, not left stale — otherwise
  // storeSupportsSelectedOrderType() empties the tab still on screen.
  if (!supportsMenuType(nextMenuType)) {
    if (selectedMenuType.value !== null) {
      setOrderType(getOrderTypeForMenuType(selectedMenuType.value))
    }
    return
  }
  selectedMenuType.value = nextMenuType
  selectedMenuId.value = null
  expandedMenuIds.value = new Set()
  menuSearchQuery.value = ''
  clearFilters()
}
watch(selectedMenuId, (id) => {
  if (id === null || id === undefined) return
  const key = String(id)
  if (!expandedMenuIds.value.has(key)) {
    const next = new Set(expandedMenuIds.value)
    next.add(key)
    expandedMenuIds.value = next
  }
})
const getCategoryItems = (categoryId: number) => {
  const baseItems = filteredMenuItems.value.filter((item) => item.categoryId === categoryId)
  const query = menuSearchQuery.value.trim().toLowerCase()
  if (!query) return baseItems
  return baseItems.filter((item) => {
    const name = item.name?.toLowerCase() ?? ''
    const description = item.description?.toLowerCase() ?? ''
    return name.includes(query) || description.includes(query)
  })
}
const searchVisibleCategories = computed(() =>
  visibleMenuCategories.value.filter((category) => getCategoryItems(category.id).length > 0)
)
const searchFeaturedCategories = computed(() =>
  searchVisibleCategories.value.filter(isFeaturedCategory)
)
const searchRegularCategories = computed(() =>
  searchVisibleCategories.value.filter((category) => !isFeaturedCategory(category))
)
const displayedFeaturedCategories = computed(() =>
  menuSearchQuery.value.trim() ? searchFeaturedCategories.value : featuredVisibleCategories.value
)
const displayedRegularCategories = computed(() =>
  menuSearchQuery.value.trim() ? searchRegularCategories.value : regularVisibleCategories.value
)
const displayedCategories = computed(() => [
  ...displayedFeaturedCategories.value,
  ...displayedRegularCategories.value
])
const hasSearchResults = computed(() => searchVisibleCategories.value.length > 0)
const getCategoryScrollActivationLine = () =>
  typeof window !== 'undefined' && window.matchMedia('(min-width: 1024px)').matches ? 100 : 180
const resetActiveCategoryToFirst = () => {
  activeCategory.value = displayedCategories.value[0]?.id ?? null
}
const updateActiveCategoryFromViewport = () => {
  if (isScrollingToCategory.value) return
  const categories = displayedCategories.value
  if (categories.length === 0) {
    activeCategory.value = null
    return
  }
  const activationLine = getCategoryScrollActivationLine()
  let currentId = categories[0]!.id
  for (const category of categories) {
    const element = document.getElementById(`category-${category.id}`)
    if (element && element.getBoundingClientRect().top <= activationLine) {
      currentId = category.id
    }
  }
  activeCategory.value = currentId
}
const onCategoryScrollSpy = () => {
  if (scrollSpyRaf !== null) return
  scrollSpyRaf = requestAnimationFrame(() => {
    scrollSpyRaf = null
    updateActiveCategoryFromViewport()
  })
}
const attachCategoryScrollSpy = () => {
  if (!import.meta.client) return
  detachCategoryScrollSpy()
  window.addEventListener('scroll', onCategoryScrollSpy, { passive: true })
  updateActiveCategoryFromViewport()
}
const detachCategoryScrollSpy = () => {
  if (!import.meta.client) return
  window.removeEventListener('scroll', onCategoryScrollSpy)
  if (scrollSpyRaf !== null) {
    cancelAnimationFrame(scrollSpyRaf)
    scrollSpyRaf = null
  }
}
const isMenuExpanded = (menuId: number | string) => {
  return expandedMenuIds.value.has(String(menuId))
}
const toggleMenuExpansion = (menuId: number | string) => {
  const key = String(menuId)
  const next = new Set(expandedMenuIds.value)
  if (next.has(key)) next.delete(key)
  else next.add(key)
  expandedMenuIds.value = next
}
const changeMenu = (menuId: number | string) => {
  selectedMenuId.value = menuId
  const key = String(menuId)
  if (!expandedMenuIds.value.has(key)) {
    const next = new Set(expandedMenuIds.value)
    next.add(key)
    expandedMenuIds.value = next
  }
}
const mobileMenuOptions = computed(() =>
  menus.value.map((menu) => {
    const reason = getMenuUnavailableReason(menu)
    return { label: reason ? `${menu.name} - ${reason}` : menu.name, value: menu.id }
  })
)
const onMobileMenuSelect = (value: string | number | undefined) => {
  if (value === undefined || value === '' || value === null) return
  const menu = menus.value.find((m) => String(m.id) === String(value))
  if (menu) changeMenu(menu.id)
}
const changeMenuAndScrollToCategory = async (menuId: number | string, categoryId: number) => {
  if (String(selectedMenuId.value) !== String(menuId)) {
    changeMenu(menuId)
    await nextTick()
  }
  scrollToCategory(categoryId)
}
const getMenuItemImage = (item: MenuItem | null | undefined): string | null =>
  resolveMenuItemImageUrl(item)
const featuredCarouselRefs = ref<Record<number, HTMLElement>>({})
const setFeaturedCarouselRef = (
  categoryId: number,
  el:
    | Element
    | {
        $el?: Element
      }
    | null
) => {
  let element: HTMLElement | null = null
  if (el instanceof HTMLElement) {
    element = el
  } else if (el && '$el' in el && el.$el instanceof HTMLElement) {
    element = el.$el
  }
  if (element) {
    featuredCarouselRefs.value[categoryId] = element
    return
  }
  featuredCarouselRefs.value = Object.fromEntries(
    Object.entries(featuredCarouselRefs.value).filter(([key]) => Number(key) !== categoryId)
  )
}
const getFeaturedCarouselVisibleSlides = () => {
  if (!import.meta.client) return 2
  const width = window.innerWidth
  if (width >= 1024) return 4
  return 2
}
const featuredCarouselHasPagination = (categoryId: number) =>
  getCategoryItems(categoryId).length > getFeaturedCarouselVisibleSlides()
const scrollFeaturedCarousel = (categoryId: number, direction: CarouselDirection) => {
  const container = featuredCarouselRefs.value[categoryId]
  if (!container) return
  const items = container.querySelectorAll<HTMLElement>('.featured-carousel-item')
  if (items.length === 0) return
  const visibleSlides = getFeaturedCarouselVisibleSlides()
  const itemWidth = items[0]!.getBoundingClientRect().width
  const gap =
    Number.parseFloat(getComputedStyle(container).columnGap) ||
    Number.parseFloat(getComputedStyle(container).gap) ||
    20
  const scrollWidth = itemWidth * visibleSlides + gap * (visibleSlides - 1)
  scrollCarouselStep(container, direction, scrollWidth)
}
const scrollFeaturedPrev = (categoryId: number) => {
  scrollFeaturedCarousel(categoryId, CarouselDirection.PREV)
}
const scrollFeaturedNext = (categoryId: number) => {
  scrollFeaturedCarousel(categoryId, CarouselDirection.NEXT)
}
const getMenuItemPriceTypeLabel = (item: MenuItem): string | null => {
  return item.price_type === undefined ? null : priceTypeCopy.unitLabel(item.price_type)
}
watch(selectedMenuId, () => {
  resetActiveCategoryToFirst()
})
watch(
  () => [filters.value.individualPackaging, filters.value.dietaryOptions.join(',')],
  () => {
    resetActiveCategoryToFirst()
  }
)
watch(menuSearchQuery, () => {
  resetActiveCategoryToFirst()
})
watch(
  () => [hasMenus.value, menusLoading.value, displayedCategories.value.map((c) => c.id).join(',')],
  async ([hasMenusVal, menusLoadingVal]) => {
    if (hasMenusVal !== true || menusLoadingVal === true) {
      detachCategoryScrollSpy()
      return
    }
    resetActiveCategoryToFirst()
    await nextTick()
    attachCategoryScrollSpy()
  },
  { flush: 'post' }
)
watch(menus, (list) => {
  if (list.length === 0) {
    selectedMenuId.value = null
    return
  }
  if (!list.some((menu) => String(menu.id) === String(selectedMenuId.value))) {
    selectedMenuId.value = list[0]?.id ?? null
  }
})
// Lifecycle
onMounted(async () => {
  restoreContext()
  // Set the current store for the store cart
  const storeSlug = slug.value
  if (storeSlug != null && storeSlug !== '') {
    logger.debug('onMounted', { storeSlug })
  }
  // Fetch the store first so a temporary closure wins over menu-loading errors.
  await fetchStoreDetails()
  if (initializationController.signal.aborted) return
  const ready = await waitForStoreContext(store, error, slug, initializationController.signal)
  if (!ready || initializationController.signal.aborted) return
  if (error.value != null || store.value === null) {
    menusLoading.value = false
    return
  }
  // Load menus/locations even while the closure banner is showing so a later date/time change
  // that flips it off finds menusFetchStarted already true, keeping the key watcher live.
  reconcileMenuTypeAfterStoreLoad()
  await Promise.all([fetchStoreMenus(), fetchLocations()])
  if (initializationController.signal.aborted || isFullPageBlockVisible.value) return
  if (selectedOrderContext.value.needsGuestCount) {
    // A direct-link, brand, favorites, or SEO visitor has no stored party size: ask before checking.
    openGuestCountPrompt()
  } else if (orderDetailsMissingReason.value === '') {
    // refreshAvailability never throws -- failures are already surfaced internally.
    await refreshAvailability({ checkMenu: true })
  }
})
watch(
  () => selectedOrderContext.value.orderType,
  (orderType, previousOrderType) => {
    if (orderType === previousOrderType) {
      return
    }
    syncMenuTypeWithOrderType(orderType)
  }
)
onUnmounted(() => {
  detachCategoryScrollSpy()
  if (programmaticScrollTimer) clearTimeout(programmaticScrollTimer)
})
</script>

<style scoped>
.store-content-height {
  min-height: calc(100vh - 80px);
}

.store-sidebar-height {
  height: calc(100vh - 100px);
}

.featured-carousel.scrollbar-hide::-webkit-scrollbar {
  display: none;
}
</style>
